import { DatePipe, DecimalPipe, registerLocaleData } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import localeItExtra from '@angular/common/locales/extra/it';
import localeIt from '@angular/common/locales/it';
import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';
import { MatMomentDateModule, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, RouterModule, Routes } from '@angular/router';
import { FlamingoSharedModule } from '@flamingo/flamingo-shared.module';
import { FlamingoModule } from '@flamingo/flamingo.module';
import { FlamingoAuthGuard } from '@flamingo/service/flamingo-auth.guard';
import { FlamingoAuthService } from '@flamingo/service/flamingo-auth.service';
import { FuseProgressBarModule, FuseSidebarModule, FuseThemeOptionsModule } from '@fuse/components';
import { FuseModule } from '@fuse/fuse.module';
import { FuseSharedModule } from '@fuse/shared.module';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { AppComponent } from 'app/app.component';
import { fuseConfig } from 'app/fuse-config';
import { LayoutModule } from 'app/layout/layout.module';
import { CurrencyMaskInputMode, NgxCurrencyModule } from 'ngx-currency';
import { NgxFileDropModule } from 'ngx-file-drop';
import { initializeApp } from './app.startup';
import {
  AuctionBidDialogComponent,
} from './children/main/children/buyer/children/auction-progress/component/auction-bid-dialog/auction-bid-dialog.component';
import {
  AuctionBidMassiveDialogComponent,
} from './children/main/children/buyer/children/auction-progress/component/auction-bid-massive-dialog/auction-bid-massive-dialog.component';
import {
  WalletTransactionDialogComponent,
} from './children/main/children/buyer/component/wallet-transaction-dialog/wallet-transaction-dialog.component';
import {
  EditDocumentDialogComponent,
} from './children/main/children/seller/children/auction-seller/children/auction-seller/component/edit-document-dialog/edit-document-dialog.component';
import {
  ClientDialogComponent,
} from './children/main/children/seller/children/client/component/client-dialog/client-dialog.component';
import {
  NewInvoiceDialogComponent,
} from './children/main/children/seller/children/invoice/children/new-invoice-dialog/new-invoice-dialog.component';
import {
  PublishInvoiceModule,
} from './children/main/children/seller/children/invoice/publish-invoice/publish-invoice.module';
import {
  SelectDebtorDialogComponent,
} from './children/main/children/seller/children/seller-document/select-debtor-dialog/select-debtor-dialog.component';
import { MainService } from './children/main/main.service';
import { CoreModule } from './core/core.module';
import { UserType } from './core/model/user.model';
import { TaxCodeDataService } from './core/service/tax-code.data.service';
import { NavigationService } from './navigation/navigation.service';

registerLocaleData(localeIt, 'it', localeItExtra);

export const MY_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MM YYYY',
    dateA11yLabel: 'DD/MM/YYYY',
    monthYearA11yLabel: 'MM YYYY',
  },
};

const appRoutes: Routes = [
  {
    path: '',
    children: [
      {
        path: '',
        canActivate: [FlamingoAuthGuard],
        loadChildren: () => import('./children/main/main.module').then(m => m.MainModule),
      },
      {
        path: 'auth',
        loadChildren: () => import('./children/authentication/authentication.module').then(m => m.AuthenticationModule),
        data: {
          type: UserType.seller,
        },
      },
    ],
  },
  {
    path: 'buyer',
    children: [
      {
        path: 'auth',
        loadChildren: () => import('./children/authentication/authentication.module').then(m => m.AuthenticationModule),
        data: {
          type: UserType.buyer,
        },
      },
      {
        path: '**',
        redirectTo: 'auth',
      },
    ],
  },
  {
    path: 'partner',
    children: [
      {
        path: 'auth',
        loadChildren: () => import('./children/authentication/authentication.module').then(m => m.AuthenticationModule),
        data: {
          type: UserType.partner,
        },
      },
      {
        path: '**',
        redirectTo: 'auth',
      },
    ],
  },
  {
    path: '**',
    redirectTo: '',
  },
];

@NgModule({
  declarations: [
    AppComponent,
    NewInvoiceDialogComponent,
    ClientDialogComponent,
    AuctionBidDialogComponent,
    AuctionBidMassiveDialogComponent,
    WalletTransactionDialogComponent,
    EditDocumentDialogComponent,
    SelectDebtorDialogComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    RouterModule.forRoot(appRoutes, { relativeLinkResolution: 'legacy' }),
    TranslateModule.forRoot(),

    // Material
    MatAutocompleteModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatDialogModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatMomentDateModule,
    MatSelectModule,
    MatStepperModule,
    MatTableModule,
    MatTooltipModule,

    // Fuse modules
    FuseModule.forRoot(fuseConfig),
    FuseProgressBarModule,
    FuseSharedModule,
    FuseSidebarModule,
    FuseThemeOptionsModule,
    LayoutModule,

    // Flamingo modules
    FlamingoModule.forRoot(),
    FlamingoSharedModule,

    // Core module
    CoreModule,

    NgxFileDropModule,
    NgxCurrencyModule.forRoot({
      align: 'right',
      allowNegative: false,
      allowZero: true,
      decimal: ',',
      precision: 2,
      prefix: '',
      suffix: ' ',
      thousands: '.',
      nullable: true,
      min: null,
      max: null,
      inputMode: CurrencyMaskInputMode.NATURAL,
    }),

    PublishInvoiceModule,
    MatCardModule,
  ],
  exports: [RouterModule],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'it' },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    { provide: LOCALE_ID, useValue: 'it' },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      multi: true,
      deps: [FlamingoAuthService, MainService, NavigationService, Router],
    },
    { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: { disableTooltipInteractivity: true } },
    TaxCodeDataService,
    DecimalPipe,
    DatePipe,
    TranslatePipe,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
}
